import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { debounce, type DebouncedFunc } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { IScenarioWeightedRunResponseDataModel } from '../../../../data-models/scenario-run.data-model';
import { IScenarioDataModel } from '../../../../data-models/scenario.data-model';
import {
  runWeightedScenarios,
  updateScenarioWeight,
} from '../../../../services/queries/MaggieScenarioQueries';
import { getErrorMessage } from '../../../../services/queryHelpers';
import { scenariosByCompanyIdStateJ } from '../../../../services/state/ScenariosStateJ';

export type AllocationState = 'under' | 'over' | 'complete';

const ALLOCATION_EPSILON = 1e-6;

export function getAllocationTotal(scenarios: IScenarioDataModel[]): number {
  return scenarios.reduce((total, scenario) => total + (scenario.probabilityWeight ?? 0), 0);
}

// Patches one scenario's weight in the company's cached scenario list.
function writeScenarioWeight(
  get: Getter,
  set: Setter,
  companyId: number,
  scenarioId: number,
  weight: number | null
): void {
  const current = get(scenariosByCompanyIdStateJ(companyId));
  if (!Array.isArray(current)) {
    return;
  }
  set(
    scenariosByCompanyIdStateJ(companyId),
    current.map((scenario) =>
      scenario.id === scenarioId ? { ...scenario, probabilityWeight: weight } : scenario
    )
  );
}

export interface IUseWeightedScenarios {
  allocationTotal: number;
  allocationState: AllocationState;
  isComplete: boolean;
  weightedResult: IScenarioWeightedRunResponseDataModel | null;
  updateWeight: (scenarioId: number, weight: number | null) => void;
  runWeighted: () => Promise<void>;
}

/**
 * Drives the Weighted Outcome experience for a company's scenario set: tracks the
 * allocation total, auto-saves each `% Chance` edit (debounced) with an optimistic
 * cache update, runs the server-side blend, and clears the result whenever a weight
 * changes so the row returns to its disabled state until the total is 100 again.
 */
export function useWeightedScenarios(
  companyId: number,
  scenarios: IScenarioDataModel[]
): IUseWeightedScenarios {
  const { pushErrorToast } = useToastMessageState();
  const [weightedResult, setWeightedResult] = useState<IScenarioWeightedRunResponseDataModel | null>(null);

  const allocationTotal = useMemo(() => getAllocationTotal(scenarios), [scenarios]);
  // A blank weight counts as 0 (excluded from the blend), so only the running total must reach 100.
  const isComplete = scenarios.length > 0 && Math.abs(allocationTotal - 100) < ALLOCATION_EPSILON;
  const allocationState: AllocationState =
    allocationTotal > 100 + ALLOCATION_EPSILON ? 'over' : isComplete ? 'complete' : 'under';

  // Last known-good (server-saved) weight per scenario — the value a failed persist rolls back to.
  // Seeded lazily on first edit and advanced on each successful save, so it stays correct even when
  // an earlier in-flight persist resolves between a later persist's start and its failure.
  const rollbackBaselineRef = useRef(new Map<number, number | null>());

  // Scenarios with a weight save in flight. The Weighted Outcome only auto-runs once these settle —
  // the server reads *persisted* weights, so running mid-save would 422 on stale weights.
  const pendingPersistsRef = useRef(new Set<number>());
  const [pendingPersists, setPendingPersists] = useState(0);
  const markPending = useCallback((scenarioId: number, pending: boolean) => {
    if (pending) {
      pendingPersistsRef.current.add(scenarioId);
    } else {
      pendingPersistsRef.current.delete(scenarioId);
    }
    setPendingPersists(pendingPersistsRef.current.size);
  }, []);

  const revertWeight = useAtomCallback(
    useCallback(
      (get: Getter, set: Setter, scenarioId: number, weight: number | null) =>
        writeScenarioWeight(get, set, companyId, scenarioId, weight),
      [companyId]
    )
  );

  const persistWeight = useMemo(() => {
    // One debounce per scenario: editing different scenarios within the window must not coalesce and drop each other.
    const debouncedByScenario = new Map<number, DebouncedFunc<(weight: number | null) => void>>();
    const persist = (scenarioId: number, weight: number | null) => {
      let debounced = debouncedByScenario.get(scenarioId);
      if (!debounced) {
        debounced = debounce(async (next: number | null) => {
          try {
            await updateScenarioWeight(scenarioId, next);
            // Saved successfully — this becomes the new rollback target (don't delete, or a later
            // overlapping persist's failure would roll back to a stale/empty value).
            rollbackBaselineRef.current.set(scenarioId, next);
          } catch (err) {
            const previous = rollbackBaselineRef.current.get(scenarioId) ?? null;
            revertWeight(scenarioId, previous);
            pushErrorToast({ message: getErrorMessage(err, 'Error saving probability weight') });
          } finally {
            markPending(scenarioId, false);
          }
        }, 800);
        debouncedByScenario.set(scenarioId, debounced);
      }
      debounced(weight);
    };
    return Object.assign(persist, {
      cancelAll: () => debouncedByScenario.forEach((debounced) => debounced.cancel()),
    });
  }, [pushErrorToast, revertWeight, markPending]);

  // Cancel pending persists on unmount (or when the closure is replaced) so none fire after teardown.
  useEffect(() => () => persistWeight.cancelAll(), [persistWeight]);

  const updateWeight = useAtomCallback(
    useCallback(
      (get: Getter, set: Setter, scenarioId: number, weight: number | null) => {
        const current = get(scenariosByCompanyIdStateJ(companyId));
        if (Array.isArray(current) && !rollbackBaselineRef.current.has(scenarioId)) {
          const existing = current.find((scenario) => scenario.id === scenarioId);
          rollbackBaselineRef.current.set(scenarioId, existing?.probabilityWeight ?? null);
        }
        writeScenarioWeight(get, set, companyId, scenarioId, weight);
        // Any weight edit invalidates a previously computed Weighted Outcome.
        setWeightedResult(null);
        markPending(scenarioId, true);
        persistWeight(scenarioId, weight);
      },
      [companyId, persistWeight, markPending]
    )
  );

  const runWeighted = useCallback(async () => {
    try {
      const data = await runWeightedScenarios(companyId);
      setWeightedResult(data);
    } catch (err) {
      pushErrorToast({ message: getErrorMessage(err, 'Error computing weighted outcome') });
    }
  }, [companyId, pushErrorToast]);

  // Auto-compute the Weighted Outcome once the allocation reaches 100% (no explicit Run button) and
  // all weight saves have settled — the server reads persisted weights, so we must not race the save.
  // `updateWeight` clears the result + marks the scenario pending, so this recomputes after each
  // edit's save lands while the total is still 100.
  useEffect(() => {
    if (!isComplete || weightedResult || pendingPersists > 0) {
      return;
    }
    const timer = setTimeout(() => void runWeighted(), 300);
    return () => clearTimeout(timer);
  }, [isComplete, weightedResult, pendingPersists, runWeighted]);

  return { allocationTotal, allocationState, isComplete, weightedResult, updateWeight, runWeighted };
}
